import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { precargarConfiguracionContratos } from './hooks/useNavigation';
import Login from './components/Login';
import CompanySelector from './components/CompanySelector';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import ProtectedRoute from './components/ProtectedRoute';
import RoleBasedRedirect from './components/RoleBasedRedirect';
import MigrationNotice from './components/MigrationNotice';
import Dashboard from './pages/Dashboard';
import Empleados from './pages/Empleados';
import NuevoEmpleado from './pages/NuevoEmpleado';
import EditarEmpleado from './pages/EditarEmpleado';
import Tareos from './pages/Tareos';
import Autorizaciones from './pages/Autorizaciones';
import Planillas from './pages/Planillas';
import PlanillaDetalle from './pages/PlanillaDetalle';
import Reportes from './pages/Reportes';
import Configuracion from './pages/Configuracion';
import Calculos from './pages/Calculos';
import Plantillas from './pages/Plantillas';


function App() {
  const { 
    user, 
    selectedCompany,
    empresaIdFromUrl,
    isLoading,
    isResolvingCompany,
    isAuthenticated,
    logout,
    selectCompany,
    clearCompanySelection,
    forceCompanySelector,
  } = useAuth();

  console.log('🚀 App.js - Renderizado. Estado actual:');
  console.log('👤 Usuario:', user ? `${user.name} (${user.email})` : 'No autenticado');
  console.log('🏢 Empresa URL:', empresaIdFromUrl || 'ninguna');
  console.log('🏢 Empresa estado:', selectedCompany ? selectedCompany.name : 'No seleccionada');
  console.log('🔍 isAuthenticated:', isAuthenticated);
  console.log('⏳ isLoading:', isLoading);
  console.log('🎯 DECISIÓN DE RENDERIZADO - Evaluando condiciones...');
  console.log('📋 Condición 1 - isLoading:', isLoading, '(Si es true, se mostrará pantalla de carga)');
  console.log('📋 Condición 2 - !isAuthenticated || !user:', !isAuthenticated || !user, '(Si es true, se mostrará Login)');
  console.log('📋 Condición 3 - selector empresa:', isAuthenticated && user && !empresaIdFromUrl);

  // Efecto para debug y forzar re-renders cuando cambie el estado de auth
  useEffect(() => {
    console.log('🔄 App.js - useEffect ejecutado por cambio de estado');
    console.log('📊 Nuevo estado:', { 
      user: user ? `${user.name} (${user.email})` : null, 
      isAuthenticated, 
      selectedCompany: selectedCompany ? selectedCompany.name : null, 
      isLoading 
    });
    
    // Log de cambios de estado para debug
    console.log('🔄 App.js - useEffect ejecutado - Estado:', {
      isAuthenticated,
      user: user?.name || 'null',
      selectedCompany: selectedCompany?.name || 'null',
      isLoading
    });
  }, [user, isAuthenticated, selectedCompany, isLoading]);

  // Al cargar el sistema (o cambiar de empresa), consultar si el módulo de contratos/plantillas está activo
  useEffect(() => {
    if (isAuthenticated && selectedCompany?.id) {
      precargarConfiguracionContratos(selectedCompany.id);
    }
  }, [isAuthenticated, selectedCompany?.id]);

  // El login ahora fuerza una recarga de página, así que no necesitamos listener de eventos

  // Función para manejar selección de empresa
  const handleSelectCompany = (companyData) => {
    console.log('🏢 App.js - handleSelectCompany llamado:', companyData);
    selectCompany(companyData);
  };

  // Función para manejar logout
  const handleLogout = async () => {
    console.log('🚪 App.js - handleLogout llamado');
    await logout();
  };

  // Mostrar pantalla de carga SÓLO durante la inicialización (no durante login)
  if (isLoading && !user && !isAuthenticated) {
    console.log('⏳ App.js - Mostrando pantalla de carga inicial');
    console.log('📍 RENDERIZANDO: LoadingScreen (inicialización)');
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 bg-gradient-to-br from-teal-500 to-teal-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <span className="text-white text-2xl font-bold">P</span>
          </div>
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Verificando autenticación...</p>
        </div>
      </div>
    );
  }

    // Decisión de renderizado basada en el estado actual
  if (!isAuthenticated || !user) {
    console.log('🔐 App.js - Mostrando Login (usuario no autenticado)');
    console.log('🔍 isAuthenticated:', isAuthenticated);
    console.log('👤 user:', user);
    console.log('📍 RENDERIZANDO: Login');
    return (
      <>
        <MigrationNotice />
        <Login />
      </>
    );
  }

  // Selector si no hay empresa en la URL/memoria o el usuario pidió cambiar de empresa
  if (isAuthenticated && user && (forceCompanySelector || (!empresaIdFromUrl && !selectedCompany))) {
    console.log('🏢 App.js - Condiciones para CompanySelector:');
    console.log('✅ isAuthenticated:', isAuthenticated);
    console.log('👤 user existe:', !!user, user ? `(${user.name})` : '');
    console.log('🏢 selectedCompany:', selectedCompany);
    console.log('⏳ isLoading:', isLoading);
    console.log('🎯 App.js - MOSTRANDO CompanySelector');
    console.log('📍 RENDERIZANDO: CompanySelector');
    
    return (
      <CompanySelector user={user} onSelectCompany={handleSelectCompany} />
    );
  }

  if (isAuthenticated && user && empresaIdFromUrl && (isResolvingCompany || !selectedCompany) && !forceCompanySelector) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center px-4">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Cargando empresa...</p>
        </div>
      </div>
    );
  }

  // Si hay usuario y empresa seleccionada, mostrar la aplicación principal
  console.log('🎯 App.js - Mostrando aplicación principal (usuario y empresa OK)');
  console.log('📍 RENDERIZANDO: MainApplication');
  return (
    <div className="min-h-screen bg-gray-50 flex">
        <Sidebar
          user={user}
          company={selectedCompany}
          selectedCompany={selectedCompany}
          onLogout={handleLogout}
          onChangeCompany={clearCompanySelection}
        />
        
        <div className="flex-1 lg:ml-64">
          <Routes>
            <Route path="/" element={
              <RoleBasedRedirect>
                <Header title="Panel principal" subtitle="Resumen general del sistema" user={user} company={selectedCompany} />
                <div className="p-4 lg:p-6">
                  <Dashboard />
                </div>
              </RoleBasedRedirect>
            } />
            
            <Route path="/empleados" element={
              <ProtectedRoute>
                <Header title="Empleados" subtitle="Gestión de empleados del sistema" user={user} company={selectedCompany} />
                <div className="p-4 lg:p-6">
                  <Empleados />
                </div>
              </ProtectedRoute>
            } />

            <Route path="/plantillas" element={
              <>
                <Header title="Plantillas" subtitle="Plantillas de contratos" user={user} company={selectedCompany} />
                <div className="p-4 lg:p-6">
                  <Plantillas />
                </div>
              </>
            } />
            
            <Route path="/tareos" element={
              <>
                <Header title="Tareos" subtitle="Control de asistencia y horas trabajadas" user={user} company={selectedCompany} />
                <div className="p-4 lg:p-6">
                  <Tareos />
                </div>
              </>
            } />
            
            <Route path="/autorizaciones" element={
              <>
                <Header title="Autorizaciones" subtitle="Gestión de horas extra, permisos y descansos médicos" user={user} company={selectedCompany} />
                <div className="p-4 lg:p-6">
                  <Autorizaciones />
                </div>
              </>
            } />
            
            <Route path="/planillas" element={
              <ProtectedRoute>
                <Header title="Planillas" subtitle="Gestión de planillas de pago" user={user} company={selectedCompany} />
                <div className="p-4 lg:p-6">
                  <Planillas />
                </div>
              </ProtectedRoute>
            } />
            
            <Route path="/planillas/:id" element={
              <ProtectedRoute>
                <Header title="Detalle de Planilla" subtitle="Información detallada de la planilla" user={user} company={selectedCompany} />
                <div className="p-4 lg:p-6">
                  <PlanillaDetalle />
                </div>
              </ProtectedRoute>
            } />
            
            <Route path="/calculos" element={
              <ProtectedRoute>
                <Header title="Cálculos" subtitle="Gestión de gratificaciones, CTS y liquidaciones" user={user} company={selectedCompany} />
                <div className="p-4 lg:p-6">
                  <Calculos />
                </div>
              </ProtectedRoute>
            } />
            
            <Route path="/calculos/:empresaId" element={
              <ProtectedRoute>
                <Header title="Cálculos" subtitle="Gestión de gratificaciones, CTS y liquidaciones" user={user} company={selectedCompany} />
                <div className="p-4 lg:p-6">
                  <Calculos />
                </div>
              </ProtectedRoute>
            } />
            
            <Route path="/reportes" element={
              <ProtectedRoute>
                <Header title="Reportes" subtitle="Análisis y reportes del sistema" user={user} company={selectedCompany} />
                <div className="p-4 lg:p-6">
                  <Reportes />
                </div>
              </ProtectedRoute>
            } />
            
            <Route path="/configuracion" element={
              <ProtectedRoute>
                <Header title="Configuración" subtitle="Ajustes del sistema" user={user} company={selectedCompany} />
                <div className="p-4 lg:p-6">
                  <Configuracion />
                </div>
              </ProtectedRoute>
            } />
            

            
            {/* Redirección de /login a la página principal */}
            <Route path="/login" element={<Navigate to="/" replace />} />
            
            {/* Rutas adicionales para empleados */}
            <Route path="/empleados/nuevo" element={
              <ProtectedRoute>
                <NuevoEmpleado />
              </ProtectedRoute>
            } />
            <Route path="/empleados/:id/editar" element={
              <ProtectedRoute>
                <EditarEmpleado />
              </ProtectedRoute>
            } />
            
            <Route path="/empleados/:id" element={
              <ProtectedRoute>
                <Header title="Detalle del Empleado" subtitle="Información completa del empleado" user={user} company={selectedCompany} />
                <div className="p-4 lg:p-6">
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:p-8">
                    <div className="text-center py-12">
                      <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">Funcionalidad en desarrollo</h3>
                      <p className="text-gray-600">Los detalles del empleado estarán disponibles próximamente.</p>
                    </div>
                  </div>
                </div>
              </ProtectedRoute>
            } />
          </Routes>
        </div>
    </div>
  );
}

export default App;
